<template>
  <div class="card mt-3 photo-gallery">
    <div class="flex gap-3 justify-between mb-6 row-wrapper">
      <h3 class="flex flex-col md:flex-row md:gap-1 page-header-2">
        Documents
        <span class="title-hint-text">(Click to preview)</span>
      </h3>
      <div class="flex flex-row md:items-center gap-3">
        <Spinner
          v-if="uploading"
          message="Uploading document..."
          class="whitespace-nowrap"
        />
        <div class="whitespace-nowrap">
          <SeraButton icon="fa-solid fa-files">
            <label class="mb-0 cursor-pointer">
              Upload Document
              <input
                ref="fileInput"
                type="file"
                accept=".jpg, .jpeg, .png, .pdf, .docx, .doc"
                class="hidden"
                @change="handleUpload"
              />
            </label>
          </SeraButton>
        </div>
      </div>
    </div>
    <div
      class="card-body document-gallery-items p-0 grid grid-cols-2 md:grid-cols-6 gap-4"
    >
      <div v-for="doc in documents" :key="doc.id" class="document-container">
        <a
          :href="doc.url"
          target="_blank"
          class="text-center"
          :title="doc.name"
        >
          <i class="fa-regular fa-file file-icon" />
          <span class="document-name">{{ doc.name }}</span>
        </a>
        <div class="buttons-container">
          <Tooltip placement="top" :disabled="breakpoints().sm">
            <template #trigger>
              <SeraButton
                icon="fa-regular fa-trash-alt"
                @click="handleDelete($event, doc.id)"
              />
            </template>
            <template #tooltip> Delete Document </template>
          </Tooltip>
          <span class="bottom-separator"> | </span>
          <Tooltip placement="top">
            <template #trigger>
              <SeraButton
                :icon="getVisibilityIcon(doc.customer_visible)"
                :class="`tooltip-doc-${doc.id}`"
                @click="handleVisibilityToggle(doc)"
              />
            </template>
            <template #tooltip>
              {{ getVisibilityTooltip(doc.customer_visible) }}
            </template>
          </Tooltip>
        </div>
        <div class="date-created">{{ datetime(doc.created_at) }}</div>
      </div>
    </div>
    <Modal
      v-if="deleteConfirm"
      title="Remove Document"
      @hide-modal="toggleDeleteConfirm"
    >
      <div>
        This document will be permanently deleted and you will not be able to
        undo this action. Are you sure?
      </div>
      <template #submit>
        <SeraButton
          id="submit_modal_btn"
          type="danger"
          @click="removeDocument()"
        >
          Remove Document
        </SeraButton>
      </template>
    </Modal>
  </div>
</template>

<script>
import axios from "axios";
import * as API from "serviceshift-ui/api-client/request";
import { mapActions } from "vuex";

import SeraButton from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";

export default {
  components: {
    Modal,
    Spinner,
    SeraButton
  },
  props: {
    documents: { type: Array, default: () => [] },
    jobId: { type: Number, default: null },
    uploadLink: { type: Object, default: null, required: true }
  },
  emits: ["upload-success", "delete-success"],
  data: function () {
    return {
      deleteId: null,
      deleteConfirm: false,
      uploading: false
    };
  },
  methods: {
    ...mapActions(["deleteJobDocument", "updateJobDocumentVisibility"]),
    handleDelete(e, id) {
      e.preventDefault();
      e.stopPropagation();
      this.deleteId = id;
      this.toggleDeleteConfirm();
    },
    async handleUpload(e) {
      this.uploading = true;

      const documents = await this.uploadDocuments(e.target.files);

      this.$emit(
        "upload-success",
        documents.map((wrapper) => wrapper.data)
      );

      this.$refs.fileInput.value = null;
      this.uploading = false;
    },
    removeDocument() {
      const documentId = this.deleteId;

      this.deleteJobDocument({ jobId: this.jobId, id: this.deleteId }).then(
        () => {
          this.deleteConfirm = false;

          this.$emit("delete-success", documentId);
        }
      );
      this.deleteId = null;
    },
    toggleDeleteConfirm() {
      this.deleteConfirm = !this.deleteConfirm;
    },
    async uploadDocuments(files) {
      const extensions = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        extensions.push(file.name.split(".").pop());
      }

      const response = await API.makeRequest(this.uploadLink.href, {
        method: "POST",
        data: { extensions }
      });

      const reservations = response.data;

      const allRequests = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const reservation = reservations[i];

        allRequests.push(this.uploadS3Document(file, reservation));
      }

      return Promise.all(allRequests);
    },
    async uploadS3Document(file, reservation) {
      const formData = new FormData();
      Object.keys(reservation.parameters).forEach((key) => {
        formData.append(key, reservation.parameters[key]);
      });

      // some browsers require this for the file to be ready
      // some do not support it
      if (typeof file.bytes === "function") {
        await file.bytes();
      }

      formData.append("file", file);

      // post document to S3
      const s3Axios = axios.create();
      await s3Axios.post(reservation.server_url, formData);

      // finalize with backend API
      return API.makeRequest(reservation.success_url, {
        method: "POST",
        data: { filename: file.name }
      });
    },
    handleVisibilityToggle(document) {
      const { id, customer_visible } = document;

      const new_visibility = !customer_visible;

      const params = {
        jobId: this.jobId,
        docId: id,
        customer_visible: new_visibility
      };

      this.updateJobDocumentVisibility(params).then(() => {
        document.customer_visible = new_visibility;
      });
    },
    getVisibilityIcon(customerVisible) {
      return customerVisible ? "fa fa-eye" : "fa fa-eye-slash";
    },
    getVisibilityTooltip(customerVisible) {
      return !customerVisible ? "Hidden from customer" : "Visible to customer";
    }
  }
};
</script>

<style lang="scss" scoped>
.file-icon {
  font-size: 24px;
  margin-top: 10%;
  margin-bottom: 5%;
}

.buttons-container {
  display: flex;
  flex-direction: row;
  height: 30px;
  position: relative;
  bottom: 30px;
  align-items: center;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid #dee2e5;
  border-top: 1px solid var(--neutral-gray);
  justify-content: space-evenly;
  border-radius: 0 0 4px 4px;
}

.document-name {
  font-size: 14px;
}

.document-gallery-items {
  a {
    color: black;
    display: flex;
    flex-direction: column;
    min-height: 150px;
    border-radius: 4px !important;
    border: 1px solid #dee2e5;
    padding: 10px;
    span {
      white-space: normal;
      line-height: 16px;
    }
  }
}
.date-created {
  position: relative;
  bottom: 20px;
  text-align: center;
}
</style>
